/** Reuse immutable snapshot bodies only after the server rechecks access.
 * No cached result bypasses a request; 403/404 always propagate.
 */
export class SnapshotBodyCache<T extends { id?: string | null; html?: unknown; narrative?: unknown }> {
  private entries = new Map<string, T>();
  private viewer: string | undefined;

  async read(
    key: string,
    viewer: () => string,
    fetchSnapshot: (knownId?: string) => Promise<{ snapshot: T; unchanged?: boolean }>,
  ): Promise<{ snapshot: T }> {
    const identity = viewer();
    if (identity !== this.viewer) {
      this.entries.clear();
      this.viewer = identity;
    }
    const cached = this.entries.get(key);
    const result = await fetchSnapshot(cached?.id || undefined);
    if (viewer() !== identity) throw new Error("Snapshot viewer changed");
    let snapshot = result.snapshot;
    if (result.unchanged) {
      if (!cached || cached.id !== snapshot.id) {
        // A proxy/backend mismatch must never silently produce an empty view.
        const fresh = await fetchSnapshot();
        if (viewer() !== identity) throw new Error("Snapshot viewer changed");
        if (fresh.unchanged) throw new Error("Missing snapshot body");
        snapshot = fresh.snapshot;
      } else {
        snapshot = { ...snapshot, html: cached.html, narrative: cached.narrative };
      }
    }
    this.entries.delete(key);
    this.entries.set(key, snapshot);
    // Bound retained HTML even when users visit many dashboards.
    while (this.entries.size > 8) this.entries.delete(this.entries.keys().next().value!);
    return { snapshot };
  }
}
